import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, TrendingUp, Users, BarChart2, Zap, Brain, Shield } from 'lucide-react';

const PharmaceuticalMarketing = () => {
  const useCases = [
    {
      icon: Users,
      title: 'HCP Segmentation & Next-Best-Action',
      description: 'ML models that segment healthcare professionals by prescribing behaviour, channel preference, and therapeutic area affinity — powering personalised rep and digital outreach.',
      outcomes: ['Improved HCP targeting accuracy', 'Channel-optimised engagement plans', '20–35% uplift in prescription intent'],
    },
    {
      icon: Brain,
      title: 'Medical Information AI & Content Automation',
      description: 'LLM systems that auto-generate MLR-review-ready promotional materials, medical letters, and digital content at scale — dramatically cutting time-to-market.',
      outcomes: ['80% reduction in content creation time', 'MLR-ready first drafts', 'Multi-language localisation'],
    },
    {
      icon: TrendingUp,
      title: 'Market Access & Payer Intelligence',
      description: 'AI that analyses formulary data, payer coverage trends, and competitive positioning to support market access teams with real-time intelligence.',
      outcomes: ['Formulary coverage tracking', 'Payer sentiment analysis', 'Competitive landscape monitoring'],
    },
    {
      icon: BarChart2,
      title: 'Commercial Analytics & Sales Force Effectiveness',
      description: 'Integrated commercial analytics platforms that connect CRM, sales, prescription data, and market data — giving leadership a single view of brand performance.',
      outcomes: ['Real-time brand performance dashboards', 'Territory-level SFE scoring', 'ROI attribution per channel'],
    },
    {
      icon: Zap,
      title: 'Digital & Omnichannel Marketing Automation',
      description: 'AI-orchestrated omnichannel journeys that coordinate rep visits, email, webinars, and digital ads — delivering the right message at the right time to every HCP.',
      outcomes: ['Coordinated omnichannel HCP journeys', 'Automated trigger-based communications', 'Engagement score tracking'],
    },
    {
      icon: Shield,
      title: 'Pharmacovigilance & Adverse Event Monitoring',
      description: 'NLP systems that monitor social media, literature, and support channels for adverse event signals — accelerating PV case identification and reporting.',
      outcomes: ['Automated AE signal detection', 'Reduced case processing time', 'Regulatory submission support'],
    },
  ];

  const caseStudies = [
    {
      title: 'Pharma Analytics & Commercial Intelligence',
      result: 'Built a unified commercial analytics platform for a Top 20 pharma company, reducing reporting cycle from 2 weeks to real-time.',
      link: '/case-studies/pharma-analytics',
    },
    {
      title: 'Marketing Automation for Life Sciences',
      result: 'Deployed AI-orchestrated HCP engagement programme, improving rep call effectiveness by 45% and digital engagement by 3x.',
      link: '/case-studies/marketing-automation',
    },
  ];

  const techStack = [
    'Azure ML & Databricks', 'GPT-4 with HIPAA-compliant deployment', 'IQVIA & APLD data integration',
    'Salesforce Health Cloud & Veeva CRM', 'Python / NLP pipelines', 'MLR workflow automation',
    'Power BI commercial dashboards', 'Multi-language LLM fine-tuning',
  ];

  return (
    <>
      <Helmet>
        <title>AI for Pharmaceutical Marketing | HCP Engagement, Commercial Analytics & Content AI | Agility</title>
        <meta
          name="description"
          content="AI solutions for pharmaceutical marketing teams. HCP segmentation, next-best-action, medical content automation, commercial analytics, omnichannel marketing, and pharmacovigilance AI. Production systems in 3–8 weeks."
        />
        <meta
          name="keywords"
          content="pharmaceutical marketing AI, HCP segmentation AI, pharma commercial analytics, medical content AI, omnichannel pharma marketing, pharmacovigilance AI, sales force effectiveness AI"
        />
        <link rel="canonical" href="https://agility.ai/industries/pharmaceutical-marketing" />
        <meta property="og:title" content="AI for Pharmaceutical Marketing | Agility" />
        <meta property="og:description" content="AI solutions for pharmaceutical marketing: HCP engagement, commercial analytics, and content automation." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-40 pb-24 bg-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/20 to-black pointer-events-none" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-2 mb-6">
              <span className="text-teal-400 text-sm font-medium">Industry Solution</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              AI for <span className="text-teal-400">Pharmaceutical Marketing</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
              We build AI systems that personalise HCP engagement, automate medical content, and give pharmaceutical commercial teams real-time intelligence to drive brand performance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/contact"
                className="inline-flex items-center bg-teal-500 text-white px-8 py-4 rounded-md hover:bg-teal-400 transition-colors font-semibold text-lg"
              >
                Talk to a Pharma AI Specialist <ArrowRight className="ml-2" size={20} />
              </a>
              <a
                href="/case-studies"
                className="inline-flex items-center border border-gray-600 text-white px-8 py-4 rounded-md hover:border-teal-400 hover:text-teal-400 transition-colors font-semibold text-lg"
              >
                View Case Studies
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">AI Use Cases for Pharmaceutical Marketing</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              From HCP targeting to pharmacovigilance — AI built for the unique demands of pharmaceutical commercial teams.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <div key={index} className="bg-gray-900 rounded-xl p-6 border border-gray-800 hover:border-teal-500/40 transition-colors">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-teal-400" size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{useCase.title}</h3>
                  <p className="text-gray-400 mb-4 text-sm leading-relaxed">{useCase.description}</p>
                  <ul className="space-y-2">
                    {useCase.outcomes.map((outcome, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle2 className="text-teal-400 mt-0.5 flex-shrink-0" size={14} />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">Pharmaceutical Marketing Case Studies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {caseStudies.map((cs, index) => (
              <a
                key={index}
                href={cs.link}
                className="block bg-gray-900 rounded-xl p-8 border border-gray-800 hover:border-teal-500/40 transition-colors group"
              >
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-teal-400 transition-colors">{cs.title}</h3>
                <p className="text-gray-400 mb-4 text-sm">{cs.result}</p>
                <span className="text-teal-400 text-sm font-medium inline-flex items-center gap-1">
                  Read case study <ArrowRight size={14} />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-8">Technology Stack</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {techStack.map((tech, index) => (
                <span key={index} className="bg-gray-800 text-gray-300 px-4 py-2 rounded-full text-sm border border-gray-700">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-teal-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to transform your pharmaceutical marketing with AI?</h2>
          <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
            Production-ready pharma AI in 3–8 weeks. Let's identify your highest-ROI commercial use case.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center bg-white text-teal-600 px-8 py-4 rounded-md hover:bg-gray-100 transition-colors font-semibold text-lg"
          >
            Book a Free Discovery Call <ArrowRight className="ml-2" size={20} />
          </a>
        </div>
      </section>
    </>
  );
};

export default PharmaceuticalMarketing;
